import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronDown, FiChevronUp, FiHelpCircle } = FiIcons;

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const faqs = [
    {
      question: "Why should I pay for NextLevel Assistant when I can find free tools online?",
      answer: "Free tools usually give you only one feature at a time, with limits or ads. NextLevel Assistant combines the best AI tools in one simple platform designed for small businesses. You save hours jumping between apps, avoid hidden costs, and get real support built for results, not just 'free trials.'"
    },
    {
      question: "Is the $29/month 'Playground' membership worth it?",
      answer: "Absolutely! The 'Playground' membership offers a wide range of AI applications designed to unleash your potential without breaking the bank. This membership is perfect for individuals looking to experiment and innovate with cutting edge technology at an affordable price."
    },
    {
      question: "I run a small business. How will NextLevel Assistant help me?",
      answer: "NextLevel Assistant takes care of the time-consuming marketing work so you can focus on running your business. It can generate posts, reply to reviews, create your content, whether written, images or videos. Instead of hiring an expensive team, you get an assistant that works for you to make your business more visible."
    },
    {
      question: "What if I am not tech-savvy? Is the NextLevel Assistant easy to use?",
      answer: "Yes. You don't need any technical skills. If you can type a sentence, you can use it. Everything is designed to be point-and-click, with simple prompts and ready-made templates. Think of it as having a marketing helper that understands plain English and does the heavy lifting for you."
    },
    {
      question: "How can NextLevel's assistant tools enhance my marketing strategies?",
      answer: "With features like AI-enhanced marketing proposals, better stacks and objection-crushing FAQs, NextLevelLocal helps transform your marketing strategies. These tools increase brand visibility, engage your target audience and ultimately drive results through innovative AI powered solutions."
    },
    {
      question: "Are there any limitations in the 'Playground' membership?",
      answer: "While the 'Playground' membership offers a vast array of creative tools, it is designed for individuals rather than businesses. For those needing specific business tools and services, the 'Business' and 'Marketer' levels provide additional features to drive growth and efficiency. More on the different levels below."
    }
  ];

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-24 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex justify-center mb-6">
            <SafeIcon icon={FiHelpCircle} className="w-16 h-16 text-yellow-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-300">
            Everything you need to know about NextLevel Assistant
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl overflow-hidden"
            >
              <motion.button
                onClick={() => toggleFAQ(index)}
                className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-700/50 transition-colors"
                whileHover={{ backgroundColor: "rgba(55, 65, 81, 0.5)" }}
                whileTap={{ scale: 0.98 }}
              >
                <h3 className="text-lg md:text-xl font-semibold text-white pr-4">
                  {faq.question}
                </h3>
                <motion.div
                  animate={{ rotate: activeIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <SafeIcon icon={FiChevronDown} className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                </motion.div>
              </motion.button>
              <AnimatePresence>
                {activeIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6">
                      <p className="text-gray-300 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Additional CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-yellow-400/20 to-pink-500/20 border border-yellow-400/30 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              Still have questions?
            </h3>
            <p className="text-gray-300 mb-6">
              Our support team is here to help you succeed!
            </p>
            <a
              href="mailto:contact@nextlevellocal.biz"
              className="inline-block px-8 py-3 bg-gradient-to-r from-yellow-400 to-pink-500 text-black font-bold rounded-full hover:scale-105 transition-transform"
            >
              Email Support
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;